import React, { useState } from 'react';
import { TextField, InputAdornment, Switch, MenuItem } from '@mui/material';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import LockPersonOutlinedIcon from '@mui/icons-material/LockPersonOutlined';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import CustomButton from '../../../components/buttons/CustomButton';
import { LIMIT_UPGRADE_BUTTON } from '../../../components/subscription/planLockTexts';
import PopupBase from '../../../popup/base/PopupBase';
import { USER_ROLES, HEALTH_PROFESSIONAL_LABEL } from '../../../constants/userRoles';
import personInitials from './personInitials';
import RolePicker from './RolePicker';
import styles from './UserFormPopup.module.css';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('pt-BR');
};

const normalizeId = (value) => (value == null || value === '' ? null : Number(value));
const sameEmail = (a, b) => !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

/** "13/10" a partir da data do pedido. */
const formatDayMonth = (value) => {
  if (!value) return '';
  const date = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

/**
 * Bloco da marcação "Profissional de saúde". O proprietário liga e desliga (canSetHealth);
 * o administrador, ao convidar, só deixa o pedido para o proprietário aprovar
 * (canRequestHealth); na edição, quem não é proprietário só lê a situação.
 * Pedido pendente: o proprietário decide pelos botões "Aprovar acesso" e "Recusar"
 * (onDecideRequest); salvar o resto do formulário não mexe no pedido.
 */
function HealthBlock({ isEdit, user, value, onChange, canSetHealth, canRequestHealth, saving, onDecideRequest }) {
  const hasRequest = isEdit && !user.isHealthProfessional && !!user.healthProfessionalRequestedAt;
  const pendingSince = hasRequest ? formatDate(user.healthProfessionalRequestedAt) : '';

  if (canSetHealth && hasRequest && onDecideRequest) {
    const day = formatDayMonth(user.healthProfessionalRequestedAt);
    return (
      <div className={styles.toggle}>
        <span className={styles.toggleIcon}><MedicalServicesOutlinedIcon fontSize="small" /></span>
        <span className={styles.toggleText}>
          <span className={styles.toggleTitle}>{HEALTH_PROFESSIONAL_LABEL}</span>
          <span className={styles.toggleHint}>
            {day ? `Pediu acesso aos atendimentos em ${day}.` : 'Pediu acesso aos atendimentos.'}
          </span>
        </span>
        <span className={styles.requestActions}>
          <CustomButton label="Recusar" variant="text" onClick={() => onDecideRequest(user, false)} enabled={!saving} />
          <CustomButton label="Aprovar acesso" variant="contained" style="main" onClick={() => onDecideRequest(user, true)} enabled={!saving} />
        </span>
      </div>
    );
  }

  if (canSetHealth) {
    return (
      <label className={styles.toggle}>
        <span className={styles.toggleIcon}><MedicalServicesOutlinedIcon fontSize="small" /></span>
        <span className={styles.toggleText}>
          <span className={styles.toggleTitle}>{HEALTH_PROFESSIONAL_LABEL}</span>
          <span className={styles.toggleHint}>
            {isEdit
                ? 'Abre prontuário, atendimento e anexos dos pacientes.'
                : 'Abre prontuário, atendimento e anexos dos pacientes. Já entra liberado ao aceitar o convite.'}
          </span>
        </span>
        <Switch checked={value} onChange={(e) => onChange(e.target.checked)} inputProps={{ 'aria-label': HEALTH_PROFESSIONAL_LABEL }} />
      </label>
    );
  }

  if (!isEdit && canRequestHealth) {
    return (
      <label className={styles.toggle}>
        <span className={styles.toggleIcon}><MedicalServicesOutlinedIcon fontSize="small" /></span>
        <span className={styles.toggleText}>
          <span className={styles.toggleTitle}>Pedir acesso aos atendimentos</span>
          <span className={styles.toggleHint}>
            O(A) Proprietário(a) recebe o pedido e libera a marcação {HEALTH_PROFESSIONAL_LABEL} quando quiser.
          </span>
        </span>
        <Switch checked={value} onChange={(e) => onChange(e.target.checked)} inputProps={{ 'aria-label': 'Pedir acesso aos atendimentos' }} />
      </label>
    );
  }

  if (!isEdit) return null;

  return (
    <div className={styles.toggle}>
      <span className={styles.toggleIcon}><MedicalServicesOutlinedIcon fontSize="small" /></span>
      <span className={styles.toggleText}>
        <span className={styles.toggleTitle}>{HEALTH_PROFESSIONAL_LABEL}</span>
        <span className={styles.toggleHint}>
          {user.isHealthProfessional
            ? 'Tem a marcação: abre prontuário, atendimento e anexos.'
            : pendingSince
              ? `Pediu acesso aos atendimentos em ${pendingSince}. Aguarda o(a) Proprietário(a).`
              : 'Sem a marcação. Só o(a) Proprietário(a) pode ligar.'}
        </span>
      </span>
    </div>
  );
}

/**
 * Agenda da pessoa e alcance "Só os meus pacientes" (papeis.md, 3.4 e 3.5). Só o proprietário,
 * só na edição e só com a marcação ligada: sem ela a API recusa, e desligar a marcação
 * desfaz o vínculo sozinho. E-mail igual ao da agenda é só sugestão, nunca vínculo automático.
 */
function ScheduleBlock({ user, resources, resourceId, scheduleScope, onResourceChange, onScopeChange }) {
  const current = resources.find((r) => r.id === resourceId) || null;
  const originalId = normalizeId(user.resourceId);
  // Só sugere quando a pessoa ainda não tem agenda e a agenda com o mesmo e-mail está livre
  const suggestion = resourceId == null
    ? resources.find((r) => sameEmail(r.email, user.email) && (r.linkedUserId == null || r.linkedUserId == user.id))
    : null;
  const inheritsHistory = current && current.hasHistory && resourceId !== originalId;
  const own = scheduleScope === 'own';

  return (
    <div className={styles.scheduleCard}>
      <div className={styles.scheduleHead}>
        <span className={styles.toggleIcon}><CalendarMonthOutlinedIcon fontSize="small" /></span>
        <span className={styles.toggleText}>
          <span className={styles.toggleTitle}>Agenda da pessoa</span>
          <span className={styles.toggleHint}>
            {suggestion
              ? `${suggestion.label} tem o mesmo e-mail. Vale conferir se é a agenda dela.`
              : 'Qual coluna da agenda é dessa pessoa. Conta os atendimentos dela.'}
          </span>
        </span>
      </div>
      <TextField
        select
        size="small"
        fullWidth
        value={resourceId ?? ''}
        onChange={(e) => onResourceChange(normalizeId(e.target.value))}
        className={styles.input}
        inputProps={{ 'aria-label': 'Agenda da pessoa' }}
      >
        <MenuItem value="">Sem agenda</MenuItem>
        {resources
          .filter((r) => r.active || r.id === resourceId)
          .map((r) => {
            const takenByOther = r.linkedUserId != null && r.linkedUserId != user.id;
            return (
              <MenuItem key={r.id} value={r.id} disabled={takenByOther}>
                {r.label}{takenByOther ? ' · já ligada a outra pessoa' : ''}
              </MenuItem>
            );
          })}
      </TextField>
      {inheritsHistory && (
        <span className={styles.scheduleWarning}>
          Essa agenda já tem atendimentos. Com "Só os meus pacientes", {user.name || user.email} passa a abrir o prontuário de todos eles.
        </span>
      )}
      <label className={`${styles.toggle} ${styles.toggleInner}`}>
        <span className={styles.toggleIcon}><LockPersonOutlinedIcon fontSize="small" /></span>
        <span className={styles.toggleText}>
          <span className={styles.toggleTitle}>Só os meus pacientes</span>
          <span className={styles.toggleHint}>
            {own && resourceId == null
              ? 'Sem agenda ligada, abre só os pacientes que cadastrou.'
              : 'Vê só a própria agenda e abre só o prontuário de quem passou por ela ou cadastrou.'}
          </span>
        </span>
        <Switch checked={own} onChange={(e) => onScopeChange(e.target.checked ? 'own' : 'all')} inputProps={{ 'aria-label': 'Só os meus pacientes' }} />
      </label>
    </div>
  );
}

/**
 * Convidar alguém (sem user) ou editar quem já está na equipe (com user): papel e marcação
 * "Profissional de saúde". Na edição, remover o acesso fica numa área separada, com a
 * confirmação no próprio popup.
 */
function UserFormPopup({
  user, assignableRoles, canSetHealth, canRequestHealth, resources = [], saving, error, onUpgrade,
  onClose, onInvite, onSave, onRevoke, onDecideRequest,
}) {
  const isEdit = !!user;
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [role, setRole] = useState(user?.role || USER_ROLES.COMPANY_STAFF);
  const [health, setHealth] = useState(isEdit ? !!user.isHealthProfessional : false);
  const [resourceId, setResourceId] = useState(isEdit ? normalizeId(user.resourceId) : null);
  const [scheduleScope, setScheduleScope] = useState(isEdit ? (user.scheduleScope || 'all') : 'all');
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  // O próprio proprietário abre a sua linha só para a marcação: o papel dele não se escolhe aqui
  const canChangeRole = !isEdit || assignableRoles.includes(user.role);
  // Agenda e alcance só existem para quem tem a marcação; o vínculo nasce depois do aceite
  const canLink = isEdit && canSetHealth && health;
  const linkChanged = canLink && (
    resourceId !== normalizeId(user.resourceId) || scheduleScope !== (user.scheduleScope || 'all')
  );
  const changed = !isEdit
    || (canChangeRole && role !== user.role)
    || (canSetHealth && health !== !!user.isHealthProfessional)
    || linkChanged;

  const handleSubmit = () => {
    if (isEdit) {
      onSave(user, {
        role: canChangeRole ? role : user.role,
        isHealthProfessional: canSetHealth ? health : !!user.isHealthProfessional,
        resourceId: canLink ? resourceId : normalizeId(user.resourceId),
        scheduleScope: canLink ? scheduleScope : (user.scheduleScope || 'all'),
      });
      return;
    }
    const trimmed = email.trim();
    if (!EMAIL_PATTERN.test(trimmed)) {
      setEmailError(trimmed ? 'Confira o e-mail.' : 'Informe o e-mail.');
      return;
    }
    onInvite(trimmed, role, health);
  };

  const content = (
    <div className={styles.content}>
      {isEdit ? (
        <div className={styles.person}>
          <span className={styles.avatar}>{personInitials(user.name || user.email)}</span>
          <div className={styles.personText}>
            <span className={styles.personName}>{user.name || 'Sem nome'}</span>
            <span className={styles.personEmail}>{user.email}</span>
          </div>
        </div>
      ) : (
        <div className={styles.field}>
          <label className={styles.label} htmlFor="invite-email">E-mail</label>
          <TextField
            id="invite-email"
            type="email"
            fullWidth
            size="small"
            placeholder="nome@exemplo.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
            error={!!emailError}
            helperText={emailError || 'A pessoa recebe o convite por e-mail.'}
            autoFocus
            className={styles.input}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutlineRoundedIcon fontSize="small" sx={{ color: 'var(--color-text-light)' }} />
                </InputAdornment>
              ),
            }}
          />
        </div>
      )}

      {canChangeRole && (
        <div className={styles.field}>
          <span className={styles.label}>Papel</span>
          <RolePicker value={role} onChange={setRole} options={assignableRoles} />
        </div>
      )}

      <HealthBlock
        isEdit={isEdit}
        user={user}
        value={health}
        onChange={setHealth}
        canSetHealth={canSetHealth}
        canRequestHealth={canRequestHealth}
        saving={saving}
        onDecideRequest={onDecideRequest}
      />

      {canLink && (
        <ScheduleBlock
          user={user}
          resources={resources}
          resourceId={resourceId}
          scheduleScope={scheduleScope}
          onResourceChange={setResourceId}
          onScopeChange={setScheduleScope}
        />
      )}

      {isEdit && canChangeRole && (
        <div className={styles.danger}>
          {confirmRevoke ? (
            <>
              <span className={styles.dangerText}>
                {user.name || user.email} deixa de entrar na empresa na hora. Dá para devolver o acesso depois, pela linha da pessoa em Usuários.
              </span>
              <div className={styles.dangerActions}>
                <CustomButton label="Manter" variant="text" onClick={() => setConfirmRevoke(false)} />
                <CustomButton
                  label="Remover acesso"
                  variant="contained"
                  style="error"
                  onClick={() => onRevoke(user)}
                  enabled={!saving}
                />
              </div>
            </>
          ) : (
            <button type="button" className={styles.dangerLink} onClick={() => setConfirmRevoke(true)}>
              <PersonRemoveOutlinedIcon fontSize="small" />
              Remover acesso à empresa
            </button>
          )}
        </div>
      )}
    </div>
  );

  // O erro fica no rodapé, sempre à vista, mesmo com o conteúdo rolado
  const footer = (
    <div className={styles.footerArea}>
      {error && (
        <div className={styles.error} role="alert">
          {error}
          {onUpgrade && (
            <button type="button" className={styles.errorAction} onClick={onUpgrade}>{LIMIT_UPGRADE_BUTTON}</button>
          )}
        </div>
      )}
      <div className={styles.footer}>
        <CustomButton label="Cancelar" variant="text" onClick={onClose} />
        <CustomButton
          label={isEdit ? 'Salvar' : 'Enviar convite'}
          variant="contained"
          style="main"
          onClick={handleSubmit}
          loading={saving}
          enabled={!saving && changed}
        />
      </div>
    </div>
  );

  return (
    <PopupBase
      Title={isEdit ? (canChangeRole ? 'Editar acesso' : 'Sua marcação') : 'Convidar para a equipe'}
      Content={content}
      Footer={footer}
      OnClose={onClose}
      maxWidth="sm"
    />
  );
}

export default UserFormPopup;
